import React, { useState } from "react"
import PropTypes from "prop-types"
import MomentPropTypes from "react-moment-proptypes"
import moment from "moment"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import { AssetSummary, LocationSummary } from "@swipesense/asset-components"
import { Column, SortDirection } from "react-virtualized"
import CSVLink from "../CSVLink"
import { entitiesPropTypes } from "../../utils/propTypes"
import { basicSort } from "../../utils/sort"
import Loading from "../Loading"
import "./ExposedAssetList.scss"

const ExposedAssetList = (props) => {
  const dateFormat = "M/D/YYYY • h:mma"

  const [sortBy, setSortBy] = useState("exposureDuration")
  const [sortDirection, setSortDirection] = useState(SortDirection.DESC)
  const [rowClickedProps, setRowClickedProps] = useState({})
  const sortedData = props.rows.sort(basicSort(sortBy, sortDirection))

  const noRowsMessage =
    "No exposed assets detected. Please adjust your case time range to rerun the search."

  const clickRow = ({ rowData }) => {
    setRowClickedProps({
      asset: rowData.asset,
      assetId: rowData.asset.id,
      entities: props.entities,
      startAt: props.startAt,
      endAt: props.endAt,
    })
  }
  const csvHeaders = [
    { label: "Asset ID", key: "assetId" },
    { label: "Asset Name", key: "name" },
    { label: "Asset Manufacturer", key: "manufacturer" },
    { label: "Asset Model", key: "model" },
    { label: "Serial Number", key: "serialNumber" },
    { label: "Ownership", key: "ownership" },
    { label: "Owned By", key: "ownedBy" },
    { label: "Current Location Room", key: "locationName" },
    { label: "Current Location Unit", key: "locationUnit" },
    { label: "Current Location Type", key: "locationType" },
    { label: "Time Since Detected", key: "timeSinceDetected" },
    { label: "Time Stationary", key: "timeStationary" },
    { label: "First Visit", key: "firstContact" },
    { label: "Last Visit", key: "lastContact" },
    { label: "Exposure Duration", key: "exposureDuration" },
  ]

  const csvDataMap = (asset) => ({
    name: asset.asset.name,
    manufacturer: asset.asset.manufacturer,
    model: asset.asset.modelName,
    assetId: asset.asset.assetId,
    serialNumber: asset.asset.serialNumber,
    ownership: asset.asset.ownership,
    ownedBy: asset.asset.owners.map((owner) => owner.name).join(", "),
    locationName: asset.asset.location.name,
    locationUnit: asset.asset.location.unit.name,
    locationType: asset.asset.location.locationType,
    timeSinceDetected: moment
      .duration(asset.asset.location.timeSinceDetected)
      .humanize(),
    timeStationary: moment
      .duration(asset.asset.location.timeStationary)
      .humanize(),
    firstContact: asset.firstContact.format(dateFormat),
    lastContact: asset.lastContact.format(dateFormat),
    exposureDuration: asset.exposureDuration.humanize(),
  })

  return (
    <div className="ExposedAssetList">
      <CSVLink
        data={props.rows}
        filename={`${props.caseId}_asset_list.csv`}
        headers={csvHeaders}
        mapData={csvDataMap}
      />
      <div className="wrapper">
        <DataTable
          data={sortedData}
          rowHeight={162}
          loading={props.loading}
          loadingComponent={<Loading />}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={(by, dir) => {
            setSortBy(by)
            setSortDirection(dir)
          }}
          onRowClick={clickRow}
          noRowsMessage={noRowsMessage}
        >
          <Column
            label="Exposed Assets"
            key="asset"
            dataKey="asset"
            disableSort={true}
            width={200}
            cellRenderer={({ cellData }) => (
              <AssetSummary {...cellData} deviceType="desktop" />
            )}
          />
          <Column
            label="First Visit"
            key="firstContact"
            dataKey="firstContact"
            width={0}
            cellRenderer={({ cellData }) => cellData.format(dateFormat)}
          />
          <Column
            label="Last Visit"
            key="lastContact"
            dataKey="lastContact"
            width={0}
            cellRenderer={({ cellData }) => cellData.format(dateFormat)}
          />
          <Column
            label="Exposure Duration"
            key="exposureDuration"
            dataKey="exposureDuration"
            width={0}
            headerRenderer={tooltipHeaderRenderer(
              '"Exposed" indicates a visit to a room occupied by a contagious patient or employee.'
            )}
            cellRenderer={({ cellData }) => cellData.humanize()}
          />
          <Column
            label="Current Location"
            key="location"
            dataKey="asset"
            disableSort={true}
            width={0}
            cellRenderer={({ cellData }) => (
              <LocationSummary {...cellData.location} />
            )}
          />
          <Column label="" key="" dataKey="" disableSort={true} maxWidth={10} />
        </DataTable>
      </div>
      <div className="total-results">
        {props.rows && (
          <div>
            {props.rows.length} {props.rows.length === 1 ? "result" : "results"}
          </div>
        )}
      </div>
      {React.createElement(props.onRowClickComponent, rowClickedProps)}
    </div>
  )
}

ExposedAssetList.propTypes = {
  caseId: PropTypes.string,
  loading: PropTypes.bool,
  startAt: PropTypes.string,
  endAt: PropTypes.string,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      asset: PropTypes.shape({
        ...AssetSummary.propTypes,
        ...LocationSummary.propTypes,
      }).isRequired,
      exposureDuration: MomentPropTypes.momentDurationObj,
      firstContact: MomentPropTypes.momentObj,
      lastContact: MomentPropTypes.momentObj,
    })
  ),
  entities: entitiesPropTypes,
  onRowClickComponent: PropTypes.func.isRequired,
}

ExposedAssetList.defaultProps = {
  rows: [],
}

export default ExposedAssetList
